import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  getTgSpecificOtsForCity,
  restoreCampaignPlan,
  restoreTgAttributeMap,
} from "../actions/campaign-planning/CampaignPlanningActions";
import { setPoiAndBrandInfo, setTg } from "../actions/map-view/MapViewActions";
import { getMediaPriceByIds } from "../../actions/org/OrgMediaPriceActions";
import {
  getCustomDataLayers,
  getDataLayerDetails,
} from "../../actions/org/OrgDataLayerActions";
import {
  clearCityConfig,
  getCityConfig,
} from "../../actions/org/OrgCityConfigActions";
import { getBasicCampaignDetails } from "../../actions/campaign/CampaignBasicInfoActions";
import { getCampaignPlanOverview } from "../../actions/campaign-plan/CampaignPlanActions";
import { getTgInfo } from "../../actions/org/OrgTargetGroupActions";
import {
  getTgHeatMapData,
  removeTgHeatMapData,
} from "../../actions/geo-data/GeoDataActions";

// Constants
import {
  DefaultPagination,
  OrgCityConfigTypes,
} from "../../constants/GeneralConstants";

export function useRestoreCampaignPlan(campaignId, queryCityId) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (campaignId && queryCityId) {
      dispatch(restoreCampaignPlan(campaignId, queryCityId));
      return;
    }
  }, [dispatch, campaignId, queryCityId]);

  return null;
}

export function useSetPoiBrands(poiBrandIdsMap, cityId, bbox) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (Object.keys(poiBrandIdsMap).length > 0) {
      dispatch(setPoiAndBrandInfo(poiBrandIdsMap, cityId, bbox));
    }
  }, [dispatch, JSON.stringify(poiBrandIdsMap), bbox]);

  return null;
}

export function useSetTgSelected(tgId) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (tgId) {
      dispatch(setTg(tgId));
    }
  }, [dispatch, tgId]);

  return null;
}

export function useGetMediaPrice(mediaIds) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (mediaIds.length > 0) {
      dispatch(getMediaPriceByIds(mediaIds));
    }
  }, [dispatch, JSON.stringify(mediaIds)]);

  return null;
}

export function useGetDataLayers(cityId) {
  const dispatch = useDispatch();

  const pageNumber = DefaultPagination.pageNumber,
    pageSize = DefaultPagination.pageSize;

  useEffect(() => {
    if (cityId) {
      dispatch(getCustomDataLayers(false, "", pageNumber, pageSize));
      return;
    }
  }, [dispatch, cityId]);

  return null;
}

export function useGetDataLayersDetails(dataLayerIds = []) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (dataLayerIds.length > 0) {
      dataLayerIds.forEach((id) => dispatch(getDataLayerDetails(id)));
      return;
    }
  }, [dispatch, JSON.stringify(dataLayerIds)]);

  return null;
}

export function useGetMediaTypesAndTouchPoints(cityId) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (cityId) {
      // clear config
      dispatch(clearCityConfig());

      // media types
      dispatch(getCityConfig(cityId, OrgCityConfigTypes.mediaType));

      // touch points
      dispatch(getCityConfig(cityId, OrgCityConfigTypes.touchPoint));
      return;
    }
  }, [dispatch, cityId]);

  return null;
}

export function useGetBasicCampaignDetails(isAddCity, campaignId) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAddCity && campaignId) {
      dispatch(getBasicCampaignDetails(campaignId));
      return;
    }
  }, [dispatch, campaignId]);

  return null;
}

export function useGetCampaignPlanOverview(isAddCity, campaignId) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAddCity && campaignId) {
      dispatch(getCampaignPlanOverview(campaignId));
      return;
    }
  }, [dispatch, campaignId]);

  return null;
}

// use Auto Manage Target Group Tab
export function useAutoManageTargetGroupTab(isAddCity) {
  const dispatch = useDispatch();

  // campaign plan & regionData & tgInfo
  const tgInfo = useSelector((state) => state.orgTargetGroup.tgInfo);
  const regionsData = useSelector(
    (state) => state.campaignPlanning.regionsData
  );
  const originalCityStretchesMap = useSelector(
    (state) => state.planningRoadStretches.originalCityStretchesMap
  );

  // plan overview
  const campaignPlanOverview = useSelector(
    (state) => state.campaignPlan.campaignPlanOverview
  );
  const { planByCity = {} } = campaignPlanOverview || {};
  const firstCityPlan = Object.values(planByCity)[0] || {};

  // data
  const { tgAttrMap, targetGroupId } = firstCityPlan || {};
  const { bbox, id: cityId } = regionsData || {};
  const { targetGroup } = tgInfo || {};
  const { resultLayers = [] } = targetGroup || {};
  const resPoiLayers = resultLayers.map((eachLayer) => eachLayer.poiTypeId);

  useEffect(() => {
    if (isAddCity && cityId && targetGroupId) {
      // restore tg Attributes map
      dispatch(restoreTgAttributeMap(tgAttrMap));

      // get tg info
      dispatch(getTgInfo(targetGroupId));
      return;
    }
  }, [dispatch, JSON.stringify(campaignPlanOverview)]);

  // generate heatmap
  useEffect(() => {
    if (isAddCity && targetGroupId && bbox && Object.keys(tgInfo).length > 0) {
      dispatch(removeTgHeatMapData());
      dispatch(getTgHeatMapData(resPoiLayers, bbox));
      return;
    }
  }, [dispatch, bbox, JSON.stringify(tgInfo)]);

  // get tg ots for city stretches
  useEffect(() => {
    if (
      isAddCity &&
      targetGroupId &&
      Object.keys(originalCityStretchesMap).length > 0
    ) {
      dispatch(
        getTgSpecificOtsForCity(targetGroupId, cityId, originalCityStretchesMap)
      );
      return;
    }
  }, [dispatch, JSON.stringify(originalCityStretchesMap)]);

  return null;
}
